import { useTranslation } from "react-i18next";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import { Button, Stack } from "~/common/mui";
import Modal, { ModalSize } from "~/common/components/Modal";
import { LinkButton } from "~/common/components/Link";
import { useBooleanState } from "../../../../../common/hooks/useBooleanState";
import { QuickActionMenuItem } from "./QuickActionMenuItem";
import { useSession } from "~/app/providers/SessionProvider";
import { canSwitchToNewProductVersion } from "./sessionCapabilities";

interface Props {
  className?: string;
  switchProductVersion: () => void;
  isB2BCart?: boolean;
  onInitiate?: (type: "priceReference" | "productVersion") => void;
  newProductVersionEnabled?: boolean;
}

export const SwitchProductVersions = ({
  className,
  isB2BCart = false,
  newProductVersionEnabled,
  switchProductVersion,
  onInitiate,
}: Props) => {
  const { t } = useTranslation();
  const session = useSession();

  const { val: modalOpen, setTrue: open, setFalse: close } = useBooleanState(false);

  if (!(newProductVersionEnabled ?? canSwitchToNewProductVersion(session))) {
    return null;
  }

  const onSave = () => {
    close();
    switchProductVersion();
  };

  const switchProductVersionBtn = (
    <LinkButton
      iconBefore={<SwapHorizOutlinedIcon sx={{ fontSize: 20 }} />}
      onClick={() => open()}
      data-test-id={"btn-load-all-pna"}
      className={className}
    >
      {t("cartPage.bulkUpload.btn.useNewestProductVersions")}
    </LinkButton>
  );

  const switchProductVersionBtnForBulkPopup = (
    <Button
      variant="text"
      startIcon={<SwapHorizOutlinedIcon />}
      onClick={() => {
        open();
        onInitiate?.("productVersion");
      }}
    >
      {t("cartPage.bulkUpload.btn.useNewestProductVersions")}
    </Button>
  );

  return (
    <>
      {isB2BCart ? (
        <QuickActionMenuItem>{switchProductVersionBtn}</QuickActionMenuItem>
      ) : (
        switchProductVersionBtnForBulkPopup
      )}

      {modalOpen && (
        <Modal
          size={ModalSize.SMALL}
          onClose={close}
          hideClose={true}
          title={t("cartPage.bulkUpload.btn.useNewestProductVersions")}
          footerChildren={
            <Stack gap={1} direction={"row"}>
              <Button variant={"text"} onClick={close}>
                {t("common.no")}
              </Button>
              <Button color="primary" onClick={onSave}>
                {t("common.yes")}
              </Button>
            </Stack>
          }
        >
          <p>{t("cartPage.modal.switchProductVersion.bodyContent")}</p>
        </Modal>
      )}
    </>
  );
};
